import { ShopifyHistoryNotice } from '../components/ShopifyHistory';
import { formatMoney } from '../lib/money';
// ===========================================
// ACCOUNTS (CPG Hub — pipeline + analysis over wholesale customers)
// ===========================================
// A real CRM view over the SAME customer list Wholesale uses: a stage board
// (first hello → steady reorders), a per-account panel with the money facts
// (revenue, A/R, cadence, margin, deductions, product mix) and an Analysis
// view across the portfolio. Winning an account changes a label, never the
// model — orders and invoicing pick up the exact same customer in Wholesale.
// Everything is computed client-side from orders + invoices already in Mise.

import { useMemo, useRef, useState, createContext, useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { COMMERCE_SCOPES, inCommerceScope, type CommerceScope } from '../utils/commerceHistory';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hubApi, wholesaleApi, menuApi, inventoryApi } from '../services/api';
import { Plus, X, CalendarClock, Contact, ChevronDown, ChevronRight, Repeat, TrendingUp, TrendingDown, Search } from 'lucide-react';
import clsx from 'clsx';
import {
  fmtDay, fmtDayYear, timeAgo, daysSince,
  CATEGORY_LABELS, CATEGORY_CHIP, CATEGORY_OPTIONS, parseSku, litersPerUnit, WEEKDAY_NAMES,
} from '../utils/cpg';
import {
  STAGES, STAGE_LABEL, ACTIVE_STAGES, isOverdue, orderDate,
  buildNeedsYou, buildAutoEvents, healthFlags,
} from '../components/accounts/insights';
import NeedsYouQueue from '../components/accounts/NeedsYouQueue';
import HealthChips from '../components/accounts/HealthChips';
import Timeline from '../components/accounts/Timeline';
import QuickActions from '../components/accounts/QuickActions';

const AccountCurrency = createContext('USD');
function useAccountMoney() {
  const currency = useContext(AccountCurrency);
  return {fmtMoney:(c:number)=>formatMoney(c,currency,{exact:true}),fmtMoneyShort:(c:number)=>formatMoney(c,currency)};
}
// ---- per-account facts, computed from orders/invoices/deductions already fetched
type AccountStats = {
  orders: any[]; invoices: any[]; deductions: any[];
  orderCount: number; revenueCents: number; aovCents: number;
  lastOrderAt: string | null;
  openArCents: number; openInvoices: number; oldestOpenDays: number | null;
  cadenceDays: number | null; expectedNextAt: number | null; reorderOverdueDays: number | null;
  monthly: Array<{ label: string; cents: number }>;
  momPct: number | null; // trailing 30d vs the 30d before
  avgDaysToPay: number | null; paidInvoiceCount: number;
  deductionCount: number; deductionCents: number;
  mix: Array<{ name: string; units: number; revenueCents: number; liters: number | null; perLiterCents: number | null }>;
  litersTotal: number;
  marginPct: number | null; marginCoveragePct: number;
};

function computeStats(
  customerId: string,
  allOrders: any[], allInvoices: any[], allDeductions: any[],
  costByMenuItemId: Map<string, number>,
): AccountStats {
  const orders = allOrders.filter((o) => o.customerId === customerId && o.status !== 'cancelled');
  const invoices = allInvoices.filter((i) => i.customerId === customerId && i.status !== 'void');
  const deductions = allDeductions.filter((d) => d.customerId === customerId);

  const revenueCents = orders.reduce((s, o) => s + (o.totalCents || 0), 0);
  const dates = orders.map((o) => new Date(orderDate(o)).getTime()).filter((t) => Number.isFinite(t)).sort((a, b) => a - b);
  const lastOrderAt = dates.length ? new Date(dates[dates.length - 1]).toISOString() : null;

  // Cadence: the account's own rhythm — average gap between orders. Needs 3+
  // orders to mean anything; fewer and we say nothing rather than guess.
  let cadenceDays: number | null = null, expectedNextAt: number | null = null, reorderOverdueDays: number | null = null;
  if (dates.length >= 3) {
    cadenceDays = Math.round((dates[dates.length - 1] - dates[0]) / 86400000 / (dates.length - 1));
    if (cadenceDays > 0) {
      expectedNextAt = dates[dates.length - 1] + cadenceDays * 86400000;
      const over = Math.floor((Date.now() - expectedNextAt) / 86400000);
      reorderOverdueDays = over > 0 ? over : null;
    }
  }

  // Last 6 calendar months, oldest → newest
  const monthly: Array<{ label: string; cents: number }> = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const cents = orders.reduce((s, o) => {
      const od = new Date(orderDate(o));
      return `${od.getFullYear()}-${od.getMonth()}` === key ? s + (o.totalCents || 0) : s;
    }, 0);
    monthly.push({ label: d.toLocaleDateString('en-US', { month: 'short' }), cents });
  }

  // Direction: trailing 30 days vs the 30 before — completed-window math, no seasonality claims
  const last30 = orders.reduce((s, o) => (daysSince(orderDate(o)) < 30 ? s + (o.totalCents || 0) : s), 0);
  const prior30 = orders.reduce((s, o) => { const d = daysSince(orderDate(o)); return d >= 30 && d < 60 ? s + (o.totalCents || 0) : s; }, 0);
  const momPct = prior30 > 0 ? Math.round(((last30 - prior30) / prior30) * 100) : null;

  const open = invoices.filter((i) => i.status !== 'paid');
  const openArCents = open.reduce((s, i) => s + (i.totalCents || 0) - (i.amountPaidCents || 0), 0);
  const oldestOpenDays = open.length
    ? Math.max(...open.map((i) => daysSince(i.dueAt || i.issuedAt)))
    : null;

  const paid = invoices.filter((i) => i.status === 'paid' && i.paidAt && i.issuedAt);
  const avgDaysToPay = paid.length
    ? Math.round(paid.reduce((s, i) => s + Math.max(0, (new Date(i.paidAt).getTime() - new Date(i.issuedAt).getTime()) / 86400000), 0) / paid.length)
    : null;

  const deductionCents = deductions.reduce((s, d) => s + (d.amountCents || 0), 0);

  // Product mix: units + revenue per product across all this account's orders,
  // with $/L where the product name carries a size ("12pk 250ml").
  const mixMap = new Map<string, { units: number; revenueCents: number }>();
  let costKnownRev = 0, costKnownCost = 0, costedRev = 0;
  for (const o of orders) {
    for (const it of (o.items || [])) {
      const qty = parseFloat(String(it.quantity)) || 0;
      const rev = it.totalPriceCents ?? Math.round(qty * (it.unitPriceCents || 0));
      const row = mixMap.get(it.name) || { units: 0, revenueCents: 0 };
      row.units += qty; row.revenueCents += rev;
      mixMap.set(it.name, row);
      const costCents = it.menuItemId ? costByMenuItemId.get(it.menuItemId) : undefined;
      costedRev += rev;
      if (costCents != null) { costKnownRev += rev; costKnownCost += qty * costCents; }
    }
  }
  const mix = [...mixMap.entries()]
    .map(([name, r]) => {
      const lpu = litersPerUnit(name);
      const liters = lpu != null ? Math.round(lpu * r.units * 10) / 10 : null;
      return { name, ...r, liters, perLiterCents: liters && liters > 0 ? Math.round(r.revenueCents / liters) : null };
    })
    .sort((a, b) => b.revenueCents - a.revenueCents);
  const litersTotal = Math.round(mix.reduce((s, m) => s + (m.liters || 0), 0) * 10) / 10;

  const marginPct = costKnownRev > 0 ? Math.round(((costKnownRev - costKnownCost) / costKnownRev) * 100) : null;
  const marginCoveragePct = costedRev > 0 ? Math.round((costKnownRev / costedRev) * 100) : 0;

  return {
    orders: [...orders].sort((a, b) => new Date(orderDate(b)).getTime() - new Date(orderDate(a)).getTime()),
    invoices,
    deductions: [...deductions].sort((a, b) =>
      new Date(b.deductionDate || b.createdAt || 0).getTime() - new Date(a.deductionDate || a.createdAt || 0).getTime()),
    orderCount: orders.length, revenueCents,
    aovCents: orders.length ? Math.round(revenueCents / orders.length) : 0,
    lastOrderAt, openArCents, openInvoices: open.length, oldestOpenDays,
    cadenceDays, expectedNextAt, reorderOverdueDays,
    monthly, momPct, avgDaysToPay, paidInvoiceCount: paid.length,
    deductionCount: deductions.length, deductionCents,
    mix, litersTotal, marginPct, marginCoveragePct,
  };
}

export default function AccountsPage() {
  const [currency, setCurrency] = useState('USD');
  return <AccountCurrency.Provider value={currency}><AccountsContent currency={currency} setCurrency={setCurrency}/></AccountCurrency.Provider>;
}
function AccountsContent({currency,setCurrency}:{currency:string;setCurrency:(c:string)=>void}) {
  const [params, setParams] = useSearchParams();
  const orderScope: CommerceScope = COMMERCE_SCOPES.some(s => s.value === params.get('orders')) ? params.get('orders') as CommerceScope : 'all';
  const {fmtMoneyShort} = useAccountMoney();
  const qc = useQueryClient();
  const [view, setView] = useState<'board' | 'analysis'>('board');
  const [banner, setBanner] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const say = (tone: 'success' | 'error', text: string) => setBanner({ tone, text });
  const oops = (e: any, fallback: string) => say('error', e?.response?.data?.error?.message || fallback);

  // ---- data (same keys + shapes WholesalePage uses, so the cache is shared)
  const { data: accounts = [], isPending } = useQuery({
    queryKey: ['hub-pipeline'],
    queryFn: async () => (await hubApi.getPipeline()).data?.data || [],
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['wholesale-customers'],
    queryFn: async () => (await wholesaleApi.getCustomers()).data?.data || [],
  });
  const { data: orders = [] } = useQuery({
    queryKey: ['wholesale-orders'],
    queryFn: async () => (await wholesaleApi.getOrders()).data?.data || [],
  });
  const { data: invoices = [] } = useQuery({
    queryKey: ['wholesale-invoices'],
    queryFn: async () => (await wholesaleApi.getInvoices()).data?.data || [],
  });
  const { data: deductions = [] } = useQuery({
    queryKey: ['wholesale-deductions'],
    queryFn: () => wholesaleApi.getDeductions().then((r) => r.data.data as any[]),
  });
  const { data: menuData } = useQuery({
    queryKey: ['menu-items-flat'],
    queryFn: async () => {
      const res = await menuApi.getItems();
      return res.data?.data || res.data || [];
    },
    staleTime: 60_000,
  });
  const menuItems: any[] = (Array.isArray(menuData) ? menuData : (menuData as any)?.items || []).filter((m: any) => m?.id);
  const { data: inventory = [] } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const response = await inventoryApi.getItems();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['hub-pipeline'] });

  // Unit cost per menu item: catalog SKU (from the description) → inventory item.
  // Powers the margin figures; where nothing matches we show coverage, not a guess.
  const costByMenuItemId = useMemo(() => {
    const bySku = new Map<string, number>();
    const byName = new Map<string, number>();
    for (const i of inventory as any[]) {
      const cents = Math.round((parseFloat(String(i.unitCost)) || 0) * 100);
      if (!cents) continue;
      if (i.sku) bySku.set(String(i.sku).trim().toLowerCase(), cents);
      byName.set(String(i.name || '').trim().toLowerCase(), cents);
    }
    const out = new Map<string, number>();
    for (const m of menuItems) {
      const sku = parseSku(m.description);
      const cents = (sku ? bySku.get(sku.toLowerCase()) : undefined) ?? byName.get(String(m.name || '').trim().toLowerCase());
      if (cents != null) out.set(m.id, cents);
    }
    return out;
  }, [menuItems, inventory]);

  // The board rows: pipeline (stage, activities) + the customer record
  // (category, standing weekly) + the computed money facts.
  const rows = useMemo(() => {
    const custById = new Map((customers as any[]).map((c) => [c.id, c]));
    return (accounts as any[]).filter(a => orderScope === 'all' || orders.some((o:any)=>o.customerId === a.id && inCommerceScope(o,orderScope)))
      .filter(a => !orders.some((o:any)=>o.customerId === a.id) || orders.some((o:any)=>o.customerId === a.id && (o.currencyCode || 'USD') === currency && inCommerceScope(o,orderScope))).map((a) => {
      const row: any = {
        ...a, currency, orderRelationship: orderScope === 'wholesale' ? 'Wholesale buyer' : null,
        customer: custById.get(a.id) || null,
        category: custById.get(a.id)?.category || null,
        standingWeekly: custById.get(a.id)?.standingWeekly || null,
        stats: computeStats(a.id, orders.filter((o:any)=>(o.currencyCode || 'USD') === currency && inCommerceScope(o,orderScope)), invoices.filter((i:any)=>(i.currencyCode || 'USD') === currency), deductions.filter((d:any)=>(d.currencyCode || 'USD') === currency), costByMenuItemId),
      };
      row.flags = healthFlags(row); // named flags, computed once per render of the data
      return row;
    });
  }, [accounts, customers, orders, invoices, deductions, costByMenuItemId, currency, orderScope]);

  // The morning worklist — recomputed from the same rows, no extra fetches.
  const needsYou = useMemo(() => buildNeedsYou(rows), [rows]);

  // ---- board filters: plain client-side narrowing of already-fetched rows
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState<Set<string>>(new Set());
  const [entityFilter, setEntityFilter] = useState<Set<string>>(new Set());
  const entities = useMemo(() => [...new Set(rows.map((r: any) => r.entity).filter(Boolean))] as string[], [rows]);
  const boardRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((a: any) =>
      (!q || [a.name, a.contactName, a.email].some((v) => String(v || '').toLowerCase().includes(q)))
      && (catFilter.size === 0 || (a.category && catFilter.has(a.category)))
      && (entityFilter.size === 0 || (a.entity && entityFilter.has(a.entity))));
  }, [rows, query, catFilter, entityFilter]);
  const filtering = !!query.trim() || catFilter.size > 0 || entityFilter.size > 0;
  const toggleIn = (set: Set<string>, v: string, apply: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v); else next.add(v);
    apply(next);
  };

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = rows.find((a: any) => a.id === selectedId) || null;

  const setStage = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) => hubApi.setCustomerStage(id, stage),
    onSuccess: refresh,
    onError: (e: any) => oops(e, "Couldn't move the account."),
  });

  // ---- add account (a plain wholesale customer that starts as a Lead)
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', contactName: '', email: '' });
  const addAccount = useMutation({
    mutationFn: async () => {
      const res = await wholesaleApi.createCustomer({
        name: addForm.name.trim(),
        contactName: addForm.contactName.trim() || undefined,
        email: addForm.email.trim() || undefined,
      });
      const id = res.data?.data?.id;
      if (id) await hubApi.setCustomerStage(id, 'lead');
      return id;
    },
    onSuccess: () => {
      refresh(); qc.invalidateQueries({ queryKey: ['wholesale-customers'] });
      setShowAdd(false); setAddForm({ name: '', contactName: '', email: '' });
      say('success', 'Account added as a Lead — when they buy, they are already a wholesale customer.');
    },
    onError: (e: any) => oops(e, "Couldn't add the account."),
  });

  // Pipeline header numbers: who sits where, and what the in-play accounts are
  // already worth (orders on the books — no imagined deal sizes). Follows the
  // board filters so the counts always describe what's on screen.
  const stageCounts = Object.fromEntries(STAGES.map(([s]) => [s, boardRows.filter((a: any) => a.stage === s).length]));
  const inPlay = boardRows.filter((a: any) => ACTIVE_STAGES.includes(a.stage));
  const inPlayValue = inPlay.reduce((s: number, a: any) => s + a.stats.revenueCents, 0);

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2"><Contact className="w-6 h-6 text-primary-600" /> Accounts</h1>
          <p className="text-sm md:text-base text-slate-500">The same customer list Wholesale runs on, laid out as a pipeline — first hello to steady reorders.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary flex items-center gap-2 shrink-0">
          <Plus className="w-4 h-4" /> Add account
        </button>
      </div>

      {banner && (
        <div className={clsx('flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
          banner.tone === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800')}>
          <p>{banner.text}</p>
          <button onClick={() => setBanner(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      <ShopifyHistoryNotice />
      {orders.some((o:any)=>o.source === 'shopify') && <label className="flex items-center gap-2 text-sm text-slate-500">Account orders <select aria-label="Account order type" className="input w-auto" value={orderScope} onChange={e=>{ const next=new URLSearchParams(params); next.set('orders',e.target.value); setParams(next); }}>{COMMERCE_SCOPES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}</select><span>{rows.length} accounts in this view</span></label>}
      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500"><label className="flex items-center gap-2">Account currency <select aria-label="Account currency" className="input w-auto" value={currency} onChange={e=>setCurrency(e.target.value)}>{[...new Set(['USD',...orders.map((o:any)=>o.currencyCode || 'USD')])].sort().map(c=><option key={c}>{c}</option>)}</select></label><span>{accounts.length} total accounts · showing accounts and amounts in {currency}. Sales totals cover imported history.</span></div>
      {/* View toggle */}
      <div className="flex gap-2 overflow-x-auto">
        {([['board', 'Pipeline'], ['analysis', 'Analysis']] as const).map(([id, label]) => (
          <button key={id} onClick={() => setView(id)}
            className={clsx('px-4 py-2 rounded-full text-sm font-medium shrink-0',
              view === id ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
            {label}
          </button>
        ))}
      </div>

      {showAdd && (
        <div className="card p-4 space-y-3">
          <p className="font-medium text-slate-900">New account</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Business name *" className="input" />
            <input value={addForm.contactName} onChange={(e) => setAddForm({ ...addForm, contactName: e.target.value })} placeholder="Contact name" className="input" />
            <input value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="Email" className="input" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)} className="btn btn-ghost">Cancel</button>
            <button onClick={() => addAccount.mutate()} disabled={!addForm.name.trim() || addAccount.isPending} className="btn btn-primary">
              {addAccount.isPending ? 'Adding…' : 'Add as Lead'}
            </button>
          </div>
        </div>
      )}

      {isPending ? (
        <div className="card p-8 text-center text-slate-400">Loading accounts…</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-slate-500">
          No accounts yet. Add your first prospect above — this board and the Wholesale customer list are the same records, so winning one here means it's already set up for orders.
        </div>
      ) : view === 'board' ? (
        <>
          {/* The morning worklist — most urgent across every account, above the board */}
          <NeedsYouQueue items={needsYou} onOpen={setSelectedId} />

          {/* Search + filters: narrow the board without refetching anything */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find an account…"
                className="input pl-8 w-52 text-sm" />
            </div>
            {CATEGORY_OPTIONS.map(([code, label]) => (
              <button key={code} onClick={() => toggleIn(catFilter, code, setCatFilter)}
                className={clsx('px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                  catFilter.has(code) ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                {label}
              </button>
            ))}
            {entities.length > 1 && entities.map((en) => (
              <button key={en} onClick={() => toggleIn(entityFilter, en, setEntityFilter)}
                className={clsx('px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                  entityFilter.has(en) ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                {String(en).toUpperCase()}
              </button>
            ))}
            {filtering && (
              <button onClick={() => { setQuery(''); setCatFilter(new Set()); setEntityFilter(new Set()); }}
                className="text-xs text-primary-600 font-medium">
                Clear · showing {boardRows.length} of {rows.length}
              </button>
            )}
          </div>

          {/* Pipeline header: counts per stage + what the in-play accounts are worth */}
          <div className="card p-3 md:p-4 flex flex-wrap items-center gap-x-4 gap-y-2">
            {STAGES.map(([s, label]) => (
              <span key={s} className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">{stageCounts[s]}</span> {label.toLowerCase()}
              </span>
            ))}
            <span className="ml-auto text-sm text-slate-600">
              In play (excl. won &amp; lost): <span className="font-semibold text-slate-900">{inPlay.length} account{inPlay.length === 1 ? '' : 's'}</span>
              {inPlayValue > 0 && <> · <span className="font-semibold text-slate-900">{fmtMoneyShort(inPlayValue)}</span> ordered so far</>}
            </span>
          </div>

          <Board rows={boardRows} onOpen={setSelectedId} setStage={(id, stage) => setStage.mutate({ id, stage })} stagePending={setStage.isPending} />
        </>
      ) : (
        <PortfolioAnalysis rows={rows} onOpen={(id) => { setSelectedId(id); }} />
      )}

      {selected && (
        <AccountPanel account={selected} onClose={() => setSelectedId(null)} refresh={refresh} oops={oops}
          setStage={(stage) => setStage.mutate({ id: selected.id, stage })} stagePending={setStage.isPending} menuItems={menuItems} />
      )}
    </div>
  );
}

// ===========================================
// BOARD
// ===========================================
function Board({ rows, onOpen, setStage, stagePending }: {
  rows: any[]; onOpen: (id: string) => void; setStage: (id: string, stage: string) => void; stagePending: boolean;
}) {
  // Won holds every actual customer, so it starts folded — a one-line summary
  // with the money, expandable when you want the full roster.
  const {fmtMoneyShort} = useAccountMoney();
  const [wonOpen, setWonOpen] = useState(true);
  const [lostOpen, setLostOpen] = useState(false);

  // Plain HTML5 drag between columns — the select on each card stays for
  // touch and keyboards; dragging is just the faster mouse path.
  const [dragId, setDragId] = useState<string | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);
  const endDrag = () => { setDragId(null); setOverStage(null); };
  const dropOn = (stage: string) => {
    const dragged = dragId ? rows.find((r: any) => r.id === dragId) : null;
    if (dragged && dragged.stage !== stage && !stagePending) setStage(dragged.id, stage);
    endDrag();
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-2 items-start">
      {(rows.some(r => ACTIVE_STAGES.includes(r.stage)) && !rows.some(r => r.stats.orders.some((o:any) => o.source === 'shopify')) ? STAGES : [...STAGES].sort((a,b)=>(a[0] === 'won' ? -1 : b[0] === 'won' ? 1 : 0))).map(([stage, label]) => {
        const cards = rows.filter((a: any) => a.stage === stage)
          .sort((a: any, b: any) => b.stats.revenueCents - a.stats.revenueCents);
        const folded = (stage === 'won' && !wonOpen) || (stage === 'lost' && !lostOpen);
        const toggle = stage === 'won' ? () => setWonOpen(!wonOpen) : stage === 'lost' ? () => setLostOpen(!lostOpen) : null;
        const revenue = cards.reduce((s: number, a: any) => s + a.stats.revenueCents, 0);
        const droppable = dragId != null && overStage === stage;
        return (
          <div key={stage}
            onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverStage(stage); } }}
            onDrop={(e) => { e.preventDefault(); dropOn(stage); }}
            className={clsx('shrink-0 space-y-2 rounded-xl transition-shadow', folded ? 'w-[200px]' : 'w-[256px]',
              droppable && 'ring-2 ring-primary-400 ring-offset-2 bg-primary-50/40')}>
            <div className="flex items-center justify-between px-1">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1">
                {toggle && (
                  <button onClick={toggle} className="p-0.5 -ml-1 text-slate-400 hover:text-slate-600" title={folded ? `Show ${label} accounts` : `Fold ${label}`}>
                    {folded ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                )}
                {label}
              </p>
              <span className="text-xs text-slate-400">{cards.length}</span>
            </div>
            {folded ? (
              <button onClick={toggle!} className="w-full text-left card p-3 hover:border-primary-300 transition-colors">
                <p className="text-sm font-medium text-slate-900">{cards.length} account{cards.length === 1 ? '' : 's'}</p>
                {revenue > 0 && <p className="text-xs text-slate-500">{fmtMoneyShort(revenue)} in imported orders</p>}
                <p className="text-xs text-primary-600 font-medium mt-1">{droppable ? 'Drop to move here' : `Show ${label.toLowerCase()} →`}</p>
              </button>
            ) : (
              <>
                {cards.length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-200 p-3 text-center text-xs text-slate-300">
                    {droppable ? 'Drop here' : 'Empty'}
                  </div>
                )}
                {cards.map((a: any) => (
                  <AccountCard key={a.id} a={a} muted={stage === 'lost'} onOpen={() => onOpen(a.id)}
                    setStage={(s) => setStage(a.id, s)} stagePending={stagePending}
                    dragging={dragId === a.id}
                    onDragStart={(e) => { e.dataTransfer.setData('text/plain', a.id); e.dataTransfer.effectAllowed = 'move'; setDragId(a.id); }}
                    onDragEnd={endDrag} />
                ))}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AccountCard({ a, muted, onOpen, setStage, stagePending, dragging, onDragStart, onDragEnd }: {
  a: any; muted?: boolean; onOpen: () => void; setStage: (stage: string) => void; stagePending: boolean;
  dragging?: boolean; onDragStart?: (e: React.DragEvent) => void; onDragEnd?: () => void;
}) {
  const {fmtMoney,fmtMoneyShort} = useAccountMoney();
  const st: AccountStats = a.stats;
  const lastActivityAt = (a.activities || [])[0]?.at || null;
  return (
    <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      draggable={!!onDragStart} onDragStart={onDragStart} onDragEnd={onDragEnd}
      className={clsx('w-full text-left card p-3 hover:border-primary-300 transition-colors space-y-1.5 cursor-pointer',
        muted && 'opacity-70', dragging && 'opacity-40')}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-slate-900 text-sm truncate">{a.name}</p>
        {(a.orderRelationship || a.category) && (
          <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0', CATEGORY_CHIP[a.category] || 'bg-slate-100 text-slate-600')}>
            {a.orderRelationship || CATEGORY_LABELS[a.category] || a.category}
          </span>
        )}
      </div>

      {/* The money line: open A/R beats revenue in urgency; otherwise all-time revenue */}
      {st.openArCents > 0 ? (
        <p className="text-xs text-amber-700 font-medium">{fmtMoney(st.openArCents)} owed{st.oldestOpenDays != null && st.oldestOpenDays > 0 ? ` · oldest ${st.oldestOpenDays}d` : ''}</p>
      ) : st.orderCount > 0 ? (
        <p className="text-xs text-slate-500">{fmtMoneyShort(st.revenueCents)} · {st.orderCount} order{st.orderCount === 1 ? '' : 's'}</p>
      ) : (
        <p className="text-xs text-slate-400">No active orders in this currency</p>
      )}

      {/* Health flags — hover a chip for the numbers behind it */}
      <HealthChips flags={a.flags || []} max={3} />

      {a.nextStep && (
        <p className={clsx('text-xs flex items-center gap-1', isOverdue(a.nextStep.dueAt) ? 'text-red-600 font-medium' : 'text-slate-500')}>
          <CalendarClock className="w-3 h-3 shrink-0" />
          <span className="truncate">{a.nextStep.note}{a.nextStep.dueAt ? ` · ${fmtDay(a.nextStep.dueAt)}` : ''}{isOverdue(a.nextStep.dueAt) ? ' · overdue' : ''}</span>
        </p>
      )}

      <div className="flex items-center justify-between gap-2 pt-0.5">
        <span className="text-[11px] text-slate-400">{lastActivityAt ? `Last note ${timeAgo(lastActivityAt)}` : 'No notes yet'}</span>
        <select value={a.stage} disabled={stagePending}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { e.stopPropagation(); setStage(e.target.value); }}
          className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-1 py-0.5 cursor-pointer"
          title="Move to another stage">
          {STAGES.map(([s, l]) => <option key={s} value={s}>{l}</option>)}
        </select>
      </div>
    </div>
  );
}

// ===========================================
// PORTFOLIO ANALYSIS
// ===========================================
function PortfolioAnalysis({ rows, onOpen }: { rows: any[]; onOpen: (id: string) => void }) {
  const {fmtMoney,fmtMoneyShort} = useAccountMoney();
  const buyers = rows.filter((a: any) => a.stats.orderCount > 0);

  const dueToReorder = buyers
    .filter((a: any) => a.stats.reorderOverdueDays != null && a.stage !== 'lost')
    .sort((a: any, b: any) => b.stats.reorderOverdueDays - a.stats.reorderOverdueDays);

  const topAccounts = [...buyers].sort((a: any, b: any) => b.stats.revenueCents - a.stats.revenueCents).slice(0, 8);
  const maxRevenue = topAccounts[0]?.stats.revenueCents || 1;

  const movers = buyers.filter((a: any) => a.stats.momPct != null);
  const growing = movers.filter((a: any) => a.stats.momPct > 0).sort((a: any, b: any) => b.stats.momPct - a.stats.momPct).slice(0, 5);
  const declining = movers.filter((a: any) => a.stats.momPct < 0).sort((a: any, b: any) => a.stats.momPct - b.stats.momPct).slice(0, 5);

  const slowPayers = buyers
    .filter((a: any) => a.stats.avgDaysToPay != null && a.stats.paidInvoiceCount > 0)
    .sort((a: any, b: any) => b.stats.avgDaysToPay - a.stats.avgDaysToPay)
    .slice(0, 5);

  const deductionLeaders = buyers
    .filter((a: any) => a.stats.deductionCents > 0)
    .sort((a: any, b: any) => b.stats.deductionCents - a.stats.deductionCents)
    .slice(0, 5);

  if (buyers.length === 0) {
    return (
      <div className="card p-10 text-center text-slate-500">
        Analysis builds from wholesale orders and invoices — once an account has orders on the books, its revenue, reorder rhythm and payment behavior show up here.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400">Computed from the orders and invoices recorded in Mise — nothing here is estimated.</p>

      {/* The money view: who should have ordered by now and hasn't */}
      <div className="card p-4 space-y-3">
        <div>
          <p className="font-semibold text-slate-900">Due to reorder</p>
          <p className="text-xs text-slate-500">Each account's own rhythm — average days between their orders — and who's past it. Needs 3+ orders to establish a rhythm.</p>
        </div>
        {dueToReorder.length === 0 ? (
          <p className="text-sm text-slate-400">No one's overdue — every account with a rhythm has ordered within its usual gap.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                  <th className="py-2 pr-3">Account</th>
                  <th className="py-2 pr-3">Orders every</th>
                  <th className="py-2 pr-3">Last order</th>
                  <th className="py-2 pr-3">Expected</th>
                  <th className="py-2 pr-3">Overdue</th>
                  <th className="py-2 pr-3 text-right">Typical order</th>
                </tr>
              </thead>
              <tbody>
                {dueToReorder.map((a: any) => (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => onOpen(a.id)}>
                    <td className="py-2 pr-3 font-medium text-slate-900">{a.name}</td>
                    <td className="py-2 pr-3 text-slate-600">~{a.stats.cadenceDays}d</td>
                    <td className="py-2 pr-3 text-slate-600">{fmtDay(a.stats.lastOrderAt)}</td>
                    <td className="py-2 pr-3 text-slate-600">{fmtDay(new Date(a.stats.expectedNextAt))}</td>
                    <td className="py-2 pr-3"><span className="badge badge-danger">{a.stats.reorderOverdueDays}d</span></td>
                    <td className="py-2 pr-3 text-right font-medium text-slate-900">{fmtMoneyShort(a.stats.aovCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Top accounts by order value, with volume where sizes parse */}
        <div className="card p-4 space-y-3">
          <p className="font-semibold text-slate-900">Top accounts by order value</p>
          <div className="space-y-2">
            {topAccounts.map((a: any) => (
              <button key={a.id} onClick={() => onOpen(a.id)} className="w-full text-left group">
                <div className="flex items-center justify-between text-sm gap-2">
                  <span className="text-slate-700 truncate group-hover:text-primary-600">{a.name}</span>
                  <span className="shrink-0 font-medium text-slate-900">
                    {fmtMoneyShort(a.stats.revenueCents)}
                    {a.stats.litersTotal > 0 && <span className="text-xs font-normal text-slate-400"> · {a.stats.litersTotal.toLocaleString('en-US')} L</span>}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 mt-1">
                  <div className="h-1.5 rounded-full bg-primary-500" style={{ width: `${Math.max(3, Math.round((a.stats.revenueCents / maxRevenue) * 100))}%` }} />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Growing vs declining */}
        <div className="card p-4 space-y-3">
          <div>
            <p className="font-semibold text-slate-900">Growing vs declining</p>
            <p className="text-xs text-slate-500">Last 30 days of orders against the 30 before.</p>
          </div>
          {growing.length === 0 && declining.length === 0 ? (
            <p className="text-sm text-slate-400">Not enough back-to-back months of orders to call a direction yet.</p>
          ) : (
            <div className="space-y-1.5">
              {growing.map((a: any) => (
                <button key={a.id} onClick={() => onOpen(a.id)} className="w-full flex items-center justify-between text-sm gap-2 hover:bg-slate-50 rounded px-1 -mx-1">
                  <span className="text-slate-700 truncate flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-emerald-600 shrink-0" />{a.name}</span>
                  <span className="shrink-0 text-emerald-700 font-medium">+{a.stats.momPct}%</span>
                </button>
              ))}
              {declining.map((a: any) => (
                <button key={a.id} onClick={() => onOpen(a.id)} className="w-full flex items-center justify-between text-sm gap-2 hover:bg-slate-50 rounded px-1 -mx-1">
                  <span className="text-slate-700 truncate flex items-center gap-1.5"><TrendingDown className="w-3.5 h-3.5 text-red-500 shrink-0" />{a.name}</span>
                  <span className="shrink-0 text-red-600 font-medium">{a.stats.momPct}%</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Slow payers */}
        <div className="card p-4 space-y-3">
          <div>
            <p className="font-semibold text-slate-900">Slow payers</p>
            <p className="text-xs text-slate-500">Average days from invoice to payment, worst first.</p>
          </div>
          {slowPayers.length === 0 ? (
            <p className="text-sm text-slate-400">No paid invoices yet — payment speed shows once invoices settle.</p>
          ) : (
            <div className="space-y-1.5">
              {slowPayers.map((a: any) => (
                <button key={a.id} onClick={() => onOpen(a.id)} className="w-full flex items-center justify-between text-sm gap-2 hover:bg-slate-50 rounded px-1 -mx-1">
                  <span className="text-slate-700 truncate">{a.name} <span className="text-xs text-slate-400">· {a.stats.paidInvoiceCount} paid</span></span>
                  <span className={clsx('shrink-0 font-medium', a.stats.avgDaysToPay > 45 ? 'text-red-600' : a.stats.avgDaysToPay > 30 ? 'text-amber-600' : 'text-slate-700')}>
                    {a.stats.avgDaysToPay}d avg
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Deduction leaders */}
        <div className="card p-4 space-y-3">
          <div>
            <p className="font-semibold text-slate-900">Deductions taken</p>
            <p className="text-xs text-slate-500">Who's shaving the most off invoices.</p>
          </div>
          {deductionLeaders.length === 0 ? (
            <p className="text-sm text-slate-400">No deductions recorded — they'd land here from payments recorded short on the Invoices tab.</p>
          ) : (
            <div className="space-y-1.5">
              {deductionLeaders.map((a: any) => (
                <button key={a.id} onClick={() => onOpen(a.id)} className="w-full flex items-center justify-between text-sm gap-2 hover:bg-slate-50 rounded px-1 -mx-1">
                  <span className="text-slate-700 truncate">{a.name} <span className="text-xs text-slate-400">· {a.stats.deductionCount}×</span></span>
                  <span className="shrink-0 font-medium text-amber-700">{fmtMoney(a.stats.deductionCents)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================
// ACCOUNT PANEL
// ===========================================
function AccountPanel({ account, onClose, refresh, oops, setStage, stagePending, menuItems }: {
  account: any; onClose: () => void; refresh: () => void; oops: (e: any, fallback: string) => void;
  setStage: (stage: string) => void; stagePending: boolean; menuItems: any[];
}) {
  const {fmtMoney,fmtMoneyShort} = useAccountMoney();
  const st: AccountStats = account.stats;
  const [note, setNote] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [dueAt, setDueAt] = useState('');

  // Quick-action feedback lives inside the panel (the page banner would sit
  // behind this overlay). Same tones and voice as the page banner.
  const [panelMsg, setPanelMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const sayHere = (tone: 'success' | 'error', text: string) => setPanelMsg({ tone, text });
  const oopsHere = (e: any, fallback: string) => sayHere('error', e?.response?.data?.error?.message || fallback);

  // "Log a call" just takes you to the note form — no second form to learn.
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const focusNote = () => {
    noteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    noteRef.current?.focus();
  };

  // What happened, derived from the records — woven into the timeline below.
  const autoEvents = useMemo(() => buildAutoEvents(account), [account]);

  const addActivity = useMutation({
    mutationFn: () => hubApi.addCustomerActivity(account.id, {
      note: note.trim(),
      ...(nextStep.trim() ? { nextStep: nextStep.trim() } : {}),
      ...(dueAt ? { dueAt } : {}),
    }),
    onSuccess: () => { refresh(); setNote(''); setNextStep(''); setDueAt(''); },
    onError: (e: any) => oops(e, "Couldn't save the note."),
  });
  const deleteActivity = useMutation({
    mutationFn: (activityId: string) => hubApi.deleteCustomerActivity(account.id, activityId),
    onSuccess: refresh,
    onError: (e: any) => oops(e, "Couldn't remove the note."),
  });

  const maxMonthly = Math.max(...st.monthly.map((m) => m.cents), 1);
  const cust = account.customer;
  const sw = account.standingWeekly;

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto p-5 space-y-5" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-900 truncate">{account.name}</h2>
              {(account.orderRelationship || account.category) && (
                <span className={clsx('px-1.5 py-0.5 rounded text-[10px] font-medium', CATEGORY_CHIP[account.category] || 'bg-slate-100 text-slate-600')}>
                  {account.orderRelationship || CATEGORY_LABELS[account.category] || account.category}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 truncate">
              {[account.contactName, account.email, account.phone].filter(Boolean).join(' · ') || 'No contact details yet'}
            </p>
            <p className="text-xs text-slate-400">
              {[account.paymentTerms, cust && parseFloat(cust.discountPercent) > 0 ? `${parseFloat(cust.discountPercent)}% off list` : null].filter(Boolean).join(' · ') || 'No payment terms set'}
            </p>
            <HealthChips flags={account.flags || []} className="mt-1.5" />
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg shrink-0"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        {panelMsg && (
          <div className={clsx('flex items-start justify-between gap-3 rounded-xl border px-3 py-2.5 text-sm',
            panelMsg.tone === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800')}>
            <p>{panelMsg.text}</p>
            <button onClick={() => setPanelMsg(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Stage */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Stage</p>
          <div className="flex flex-wrap gap-1.5">
            {STAGES.map(([stage, label]) => (
              <button key={stage} onClick={() => setStage(stage)} disabled={stagePending}
                className={clsx('px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                  account.stage === stage ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
                {label}
              </button>
            ))}
          </div>
          {account.stageSetAt && <p className="text-xs text-slate-400">In {STAGE_LABEL[account.stage]} since {fmtDayYear(account.stageSetAt)}</p>}
        </div>

        {/* Quick actions — results land in the timeline below as derived events */}
        <QuickActions account={account} say={sayHere} oops={oopsHere} onLogCall={focusNote} />

        {/* The numbers — computed from this account's orders and invoices */}
        {st.orderCount > 0 ? (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">How they buy</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Stat label="Imported order value" value={fmtMoneyShort(st.revenueCents)} />
              <Stat label="Avg order" value={fmtMoneyShort(st.aovCents)} sub={`${st.orderCount} orders`} />
              <Stat label="Last order" value={timeAgo(st.lastOrderAt)} sub={fmtDay(st.lastOrderAt)} />
              <Stat label="Order rhythm" value={st.cadenceDays != null ? `~${st.cadenceDays}d` : '—'}
                sub={st.cadenceDays == null ? 'needs 3+ orders' : st.reorderOverdueDays != null ? `overdue ${st.reorderOverdueDays}d` : st.expectedNextAt ? `next ~${fmtDay(new Date(st.expectedNextAt))}` : undefined}
                tone={st.reorderOverdueDays != null ? 'danger' : undefined} />
              <Stat label="Open A/R" value={st.invoices.length ? fmtMoney(st.openArCents) : 'Not available'}
                sub={st.openArCents > 0
                  ? `${st.openInvoices} open · ${(st.oldestOpenDays || 0) > 0 ? `oldest ${st.oldestOpenDays}d` : 'not due yet'}`
                  : st.invoices.length ? 'all settled' : 'no invoices imported'}
                tone={st.openArCents > 0 && (st.oldestOpenDays || 0) > 30 ? 'warning' : undefined} />
              <Stat label="Pays in" value={st.avgDaysToPay != null ? `${st.avgDaysToPay}d avg` : '—'}
                sub={st.avgDaysToPay == null ? 'no paid invoices yet' : `${st.paidInvoiceCount} paid`} />
              <Stat label="Margin" value={st.marginPct != null ? `${st.marginPct}%` : '—'}
                sub={st.marginPct != null ? `costs known for ${st.marginCoveragePct}% of revenue` : 'no unit costs matched'} />
              <Stat label="Deductions" value={st.deductionCount > 0 ? fmtMoney(st.deductionCents) : 'none'}
                sub={st.deductionCount > 0 ? `${st.deductionCount} taken` : undefined}
                tone={st.deductionCents > 0 ? 'warning' : undefined} />
              {st.litersTotal > 0 && <Stat label="Volume shipped" value={`${st.litersTotal.toLocaleString('en-US')} L`} sub="where pack sizes parse" />}
            </div>

            {/* Monthly revenue, last 6 months */}
            {st.monthly.some((m) => m.cents > 0) && (
              <div className="rounded-lg bg-slate-50 p-3">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Monthly order value — imported history</p>
                <div className="flex items-end gap-2 h-20">
                  {st.monthly.map((m, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 h-full" title={`${m.label}: ${fmtMoney(m.cents)}`}>
                      <span className="text-[10px] text-slate-500">{m.cents > 0 ? fmtMoneyShort(m.cents) : ''}</span>
                      <div className={clsx('w-full rounded-t', m.cents > 0 ? 'bg-primary-400' : 'bg-slate-200')}
                        style={{ height: `${Math.max(m.cents > 0 ? 8 : 2, Math.round((m.cents / maxMonthly) * 100) * 0.6)}%` }} />
                      <span className="text-[10px] text-slate-400">{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Product mix */}
            {st.mix.length > 0 && (
              <div className="space-y-1">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">What they buy</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                      <th className="py-1 pr-2">Product</th>
                      <th className="py-1 pr-2 text-right">Units</th>
                      <th className="py-1 pr-2 text-right">Revenue</th>
                      <th className="py-1 text-right" title="Derived from the size in the product name — blank when no size parses">$/L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {st.mix.slice(0, 8).map((m) => (
                      <tr key={m.name} className="border-t border-slate-100">
                        <td className="py-1.5 pr-2 text-slate-700">{m.name}</td>
                        <td className="py-1.5 pr-2 text-right text-slate-600">{m.units.toLocaleString('en-US')}</td>
                        <td className="py-1.5 pr-2 text-right font-medium text-slate-900">{fmtMoneyShort(m.revenueCents)}</td>
                        <td className="py-1.5 text-right text-slate-500">{m.perLiterCents != null ? fmtMoney(m.perLiterCents) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {st.mix.length > 8 && <p className="text-[11px] text-slate-400">…and {st.mix.length - 8} more products.</p>}
              </div>
            )}
            <p className="text-[11px] text-slate-400">Computed from the orders and invoices recorded in Mise — nothing here is estimated.</p>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No orders yet — once this account buys, revenue, reorder rhythm and payment behavior appear here.</p>
        )}

        {/* Standing weekly order */}
        {sw && (
          <div className="rounded-lg bg-slate-50 p-3 space-y-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
              <Repeat className="w-3.5 h-3.5 text-primary-600" /> Standing weekly order — every {WEEKDAY_NAMES[sw.weekday]}
            </p>
            <p className="text-sm text-slate-600">
              {(sw.items || []).map((it: any) => {
                const m = menuItems.find((x: any) => x.id === it.menuItemId);
                return `${it.quantity}× ${m?.name || 'product'}`;
              }).join(' · ')}
            </p>
            <p className="text-[11px] text-slate-400">Each week's order arrives as a draft in Wholesale — nothing confirms without you.</p>
          </div>
        )}

        {/* Orders & invoices */}
        {st.orderCount > 0 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Orders</p>
              {st.orders.slice(0, 6).map((o: any) => (
                <div key={o.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-slate-600 truncate">{o.orderNumber} <span className="text-xs text-slate-400">· {fmtDay(orderDate(o))}</span></span>
                  <span className="shrink-0 font-medium text-slate-900">{fmtMoneyShort(o.totalCents)}</span>
                </div>
              ))}
              {st.orders.length > 6 && <p className="text-[11px] text-slate-400">…{st.orders.length - 6} more in Wholesale.</p>}
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Invoices</p>
              {st.invoices.length === 0 ? (
                <p className="text-sm text-slate-400">None yet.</p>
              ) : st.invoices.slice(0, 6).map((i: any) => {
                const balance = (i.totalCents || 0) - (i.amountPaidCents || 0);
                return (
                  <div key={i.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="text-slate-600 truncate">{i.invoiceNumber}</span>
                    <span className="shrink-0">
                      {i.status === 'paid'
                        ? <span className="text-emerald-700 text-xs font-medium">paid</span>
                        : <span className={clsx('text-xs font-medium', daysSince(i.dueAt || i.issuedAt) > 30 ? 'text-red-600' : 'text-amber-700')}>{fmtMoney(balance)} due</span>}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500 sm:col-span-2 -mt-1">
              <Link to="/wholesale" className="text-primary-600 font-medium">Orders &amp; invoicing live in Wholesale</Link> — same customer, same records.
            </p>
          </div>
        )}

        {/* New activity */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Log an activity</p>
          <textarea ref={noteRef} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened? (call, tasting, email…)" className="input w-full min-h-[64px]" />
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <input value={nextStep} onChange={(e) => setNextStep(e.target.value)} placeholder="Next step (optional)" className="input" />
            <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} className="input" title="Next step due date" />
          </div>
          <div className="flex justify-end">
            <button onClick={() => addActivity.mutate()} disabled={!note.trim() || addActivity.isPending} className="btn btn-primary text-sm">
              {addActivity.isPending ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>

        {/* Timeline — your notes + what happened (orders, invoices, deductions), newest first */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Timeline</p>
          <Timeline activities={account.activities || []} autoEvents={autoEvents}
            onDeleteNote={(id) => deleteActivity.mutate(id)} deletePending={deleteActivity.isPending} />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'danger' | 'warning' }) {
  return (
    <div className="rounded-lg bg-slate-50 p-2.5">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
      <p className={clsx('text-sm font-bold', tone === 'danger' ? 'text-red-600' : tone === 'warning' ? 'text-amber-700' : 'text-slate-900')}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}
